import React, { useState } from "react";
import { useHistory, Link } from "react-router-dom";
import {
  Container,
  Header,
  FormField,
  Input,
  Button,
  Alert,
  SpaceBetween,
  Box,
} from "@cloudscape-design/components";
import { authService } from "../services/authService";
import PoweredByFooter from "../components/common/PoweredByFooter";

const PERSONAL_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com", "apple.com"];

export default function Register() {
  const history = useHistory();

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    registrationKey: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const companylogo = process.env.REACT_APP_CLIENT_LOGO || "/Assets/images/iWMS.png";
  const set = (field) => ({ detail }) =>
    setForm((prev) => ({ ...prev, [field]: detail.value }));

  const validate = () => {
    if (!form.username || !form.email || !form.password || !form.registrationKey) {
      return "All fields are required.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return "Please enter a valid email address.";
    }
    const domain = form.email.split("@")[1]?.toLowerCase();
    if (PERSONAL_DOMAINS.includes(domain)) {
      return "Corporate email required. Personal email addresses are not allowed.";
    }
    if (form.password.length < 6) {
      return "Password must be at least 6 characters.";
    }
    if (form.password !== form.confirmPassword) {
      return "Passwords do not match.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setLoading(true);
    try {
      await authService.register(
        form.username,
        form.email,
        form.password,
        form.registrationKey
      );
      setSuccess("Account created successfully! Redirecting to login...");
      setTimeout(() => history.push("/login"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
        <PoweredByFooter dark />
      </div>
      <div style={styles.card}>
        <SpaceBetween size="l">
          <Box textAlign="center">
            <img src={companylogo} alt="Company Logo" style={{ height: 50, marginBottom: 16 }} />
            <h1 style={styles.logo}>iWMS SmartOps</h1>
            <p style={styles.subtitle}>Create your account</p>
          </Box>

          <Container header={<Header variant="h2">Register</Header>}>
            <form onSubmit={handleSubmit}>
              <SpaceBetween size="m">
                {error && <Alert type="error">{error}</Alert>}
                {success && <Alert type="success">{success}</Alert>}

                <FormField label="Username">
                  <Input
                    value={form.username}
                    onChange={set("username")}
                    placeholder="Choose a username"
                    autoFocus
                  />
                </FormField>

                <FormField
                  label="Corporate Email"
                  description="Personal email addresses (Gmail, Yahoo, etc.) are not accepted."
                >
                  <Input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                  />
                </FormField>

                <FormField label="Password">
                  <div style={{ position: "relative" }}>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={set("password")}
                      placeholder="Minimum 6 characters"
                    />
                    <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
                      <Button variant="link" formAction="none" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? "Hide" : "Show"}
                      </Button>
                    </div>
                  </div>
                </FormField>

                <FormField label="Confirm Password">
                  <div style={{ position: "relative" }}>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={set("confirmPassword")}
                      placeholder="Re-enter your password"
                    />
                    <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
                      <Button variant="link" formAction="none" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? "Hide" : "Show"}
                      </Button>
                    </div>
                  </div>
                </FormField>

                <FormField
                  label="Registration Key"
                  description="Contact your administrator for the registration key."
                >
                  <Input
                    type="password"
                    value={form.registrationKey}
                    onChange={set("registrationKey")}
                    placeholder="Enter registration key"
                  />
                </FormField>

                <Button
                  variant="primary"
                  formAction="submit"
                  loading={loading}
                  fullWidth
                >
                  Create Account
                </Button>

                <Box textAlign="center">
                  <span style={styles.linkText}>
                    Already have an account?{" "}
                    <Link to="/login" style={styles.link}>
                      Sign in
                    </Link>
                  </span>
                </Box>
              </SpaceBetween>
            </form>
          </Container>
        </SpaceBetween>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #0f1b2d 0%, #1b2f4b 100%)",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    padding: "0 16px",
  },
  logo: {
    color: "#fff",
    fontSize: 28,
    fontWeight: 700,
    margin: 0,
    letterSpacing: 1,
  },
  subtitle: {
    color: "rgba(255,255,255,0.6)",
    marginTop: 4,
    fontSize: 14,
  },
  linkText: {
    fontSize: 14,
    color: "#666",
  },
  link: {
    color: "#0073bb",
    textDecoration: "none",
    fontWeight: 600,
  },
};
